import { useMemo } from 'react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { ROUTE_COLUMNS } from '@/app/(app)/routes/table/columns';
import { DataTable } from '@/components/data-table';
import { useEmployeesCtx } from '@/components/providers/employee-provider';
import { useApiV1Ctx } from '@/components/providers/v1-api';
import { Skeleton } from '@/components/ui/skeleton';

interface RoutesTableProps {
  /** yyyy-MM-dd */
  date?: string;
}

export function RoutesTable({ date }: RoutesTableProps) {
  const { api } = useApiV1Ctx();
  const { employeesById } = useEmployeesCtx();
  const { data, isLoading } = api.routes.searchRoutesSearchGet.useQuery(
    date ? { query: { start_at: date, limit: 100 } } : undefined
  );
  const routes = useMemo(() => {
    if (!data) return [];
    // Sort routes by employee name
    return data.sort((a, b) => {
      const aEmployee = employeesById.get(a.employee_id ?? '');
      const bEmployee = employeesById.get(b.employee_id ?? '');
      if (!aEmployee) return -1;
      if (!bEmployee) return 1;
      const r1 = aEmployee.first_name.localeCompare(bEmployee.first_name);
      if (r1 !== 0) return r1;
      const r2 = aEmployee.last_name.localeCompare(bEmployee.last_name);
      if (r2 !== 0) return r2;
      return aEmployee.id.localeCompare(bEmployee.id);
    });
  }, [data, employeesById]);

  const table = useReactTable({
    data: routes,
    columns: ROUTE_COLUMNS,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return <Skeleton className='h-96 w-full' />;
  }

  return (
    <div className='overflow-hidden rounded-md border'>
      <DataTable table={table} columns={ROUTE_COLUMNS} />
    </div>
  );
}
